import type { SensorReading } from '@esf/shared';

// Generate time-series readings for the last 7 days
function generateReadings(
  deviceId: string, assetId: string, orgId: string,
  sensorType: SensorReading['sensorType'], unit: string,
  baseValue: number, variance: number, count = 50
): SensorReading[] {
  const now = Date.now();
  const interval = (7 * 24 * 60 * 60 * 1000) / count;
  return Array.from({ length: count }, (_, i) => ({
    id: `SR-${deviceId}-${String(i + 1).padStart(3, '0')}`,
    deviceId,
    assetId,
    organisationId: orgId,
    timestamp: new Date(now - (count - i) * interval).toISOString(),
    sensorType,
    value: Math.round((baseValue + (Math.random() - 0.5) * 2 * variance) * 10) / 10,
    unit,
  }));
}

/**
 * Live telemetry samples — in-service VantageNOVA suites on Delta A350-1000,
 * a VantageSOLO on JetBlue, and Portadown line environmentals.
 *
 * OCCUPANCY_SENSOR    → seat occupancy weight (kg, 0 = vacant, ~78 = adult)
 * STRUCTURAL_MONITOR  → actuator vibration RMS (mg)
 * TEMPERATURE         → cabin / line air temperature (°C)
 * HUMIDITY            → relative humidity (%)
 * AIR_QUALITY         → AQI index
 * NOISE_LEVEL         → DTF rig acoustic dB(A)
 */
export const demoSensorReadings: SensorReading[] = [
  // VantageNOVA Suite 1A (N501DN) — occupancy in kg
  ...generateReadings('IOT-NOVA-001', 'PROD-NOVA-001', 'ORG-003', 'OCCUPANCY_SENSOR', 'kg', 42, 38),
  // VantageNOVA Suite 11K (N501DN) — actuator vibration mg
  ...generateReadings('IOT-NOVA-003', 'PROD-NOVA-003', 'ORG-003', 'STRUCTURAL_MONITOR', 'mg', 18, 12),
  // VantageNOVA Suite 1A on N502DN
  ...generateReadings('IOT-NOVA-004', 'PROD-NOVA-004', 'ORG-003', 'OCCUPANCY_SENSOR', 'kg', 39, 36),
  // VantageXL Suite 1A on N502DC — actuator vibration
  ...generateReadings('IOT-XL-001', 'PROD-XL-001', 'ORG-003', 'STRUCTURAL_MONITOR', 'mg', 24, 15),
  // VantageSOLO Suite 1A — occupancy
  ...generateReadings('IOT-SOLO-002', 'PROD-SOLO-002', 'ORG-003', 'OCCUPANCY_SENSOR', 'kg', 36, 34),
  // Portadown final-assembly cell — air temperature
  ...generateReadings('IOT-PORT-TH-01', 'PROD-NOVAFIRST-001', 'ORG-002', 'TEMPERATURE', 'C', 20.5, 1.4),
  // Portadown trim shop — humidity
  ...generateReadings('IOT-PORT-TH-02', 'PROD-DUO-001', 'ORG-002', 'HUMIDITY', '%', 48, 6),
  // Portadown FAL — air quality (cabin assembly cell)
  ...generateReadings('IOT-PORT-AQ-01', 'PROD-XLPLUSFIRST-001', 'ORG-002', 'AIR_QUALITY', 'AQI', 31, 12),

  // Cabin environmental telemetry — temperature + relative humidity per suite/asset.
  // Surfaced as the "Environment" column on the Product Registry.
  ...[
    'PROD-NOVA-001', 'PROD-NOVA-002', 'PROD-NOVA-003', 'PROD-NOVA-004',
    'PROD-XL-001', 'PROD-XL-002', 'PROD-XL-003',
    'PROD-SOLO-001', 'PROD-SOLO-002',
    'PROD-XLFIRST-001', 'PROD-XLPLUS-001', 'PROD-XLPLUS-002', 'PROD-XLPLUSFIRST-001',
    'PROD-NOVAFIRST-001', 'PROD-DUO-001', 'PROD-VANTAGE-001',
  ].flatMap(assetId => [
    ...generateReadings(`IOT-ENV-${assetId}-T`, assetId, 'ORG-003', 'TEMPERATURE', 'C', 21.5, 1.8, 24),
    ...generateReadings(`IOT-ENV-${assetId}-H`, assetId, 'ORG-003', 'HUMIDITY', '%', 46, 7, 24),
  ]),
];
